'use client';
import { Modal } from '@/components/Modal/Modal';
import PlusCircle from '@/components/icons/PlusCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import {
  FormEmployee,
  useEditEmployeeMutation,
  useGetEmployeeQuery,
  useGetListPdvQuery,
  useResetPasswordMutation,
} from '@/services/API';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCreds } from '@/store/slices/pwdResetSlice';
import { ISelectNumeroAgrement, ShortPdv } from '@/types';
import { getURLParams } from '@/utils/router';
import { useContext, useEffect, useState } from 'react';
import Form from '../form';

export default function Page() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authn_method = useAppSelector(
    (state) => state.selectedSte.societe.authn_method
  );
  const siren = useAppSelector((state) => state.selectedSte.societe.siren);
  const searchParams = getURLParams();

  const [employee, setEmployee] = useState<FormEmployee>();
  const [pdvs, setPdvs] = useState<ISelectNumeroAgrement[]>([]);

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openedResetPswModal, setOpenedResetPswModal] = useState(false);
  const { addToast } = useContext(NotifsContext);

  const {
    data: oldEmployee,
    isLoading: getOldEmpLoading,
    isError: getOldEmpError,
  } = useGetEmployeeQuery({
    siren,
    idEmployee: searchParams.get('id') || '',
  });
  const {
    data: listPdvs,
    isLoading: getListPdvsLoading,
    isError: getListPdvsError,
  } = useGetListPdvQuery(siren);
  const [editEmploye, { isLoading }] = useEditEmployeeMutation();
  const [resetPassword, { isLoading: isResetPswLoading }] =
    useResetPasswordMutation();

  const handleSubmit = () => {
    if (!employee || !oldEmployee) return;
    const operational_seller_number_list = pdvs
      .filter(({ added }) => added)
      .map(({ bnppf_pos_id }) => bnppf_pos_id);
    editEmploye({
      idEmployee: searchParams.get('id') || '',
      siren: siren,
      body: {
        ...oldEmployee,
        ...employee,
        operational_seller_number_list,
      },
    })
      .unwrap()
      .then(() => {
        addToast &&
          addToast({
            toastType: 'success',
            children: 'Confirmation de modification.',
          });
      })
      .catch((err) => {
        let errorMessage = `Une erreur est survenue.
          Rééssayer ultérieurement.`;
        try {
          if (err.data[0].error === 'uniqueness') {
            errorMessage = `Ce compte existe déjà dans l'annuaire.
              Veuillez vérifier les champs uniques`;
          }
        } catch (error) {}
        addToast &&
          addToast({
            toastType: 'error',
            children: errorMessage,
          });
      })
      .finally(() => setOpenConfirmModal(false));
  };
  useEffect(() => {
    if (oldEmployee && listPdvs) {
      setEmployee({ ...oldEmployee });
      let prepData: ISelectNumeroAgrement[] = listPdvs?.map(
        ({ bnppf_pos_id, name, partner_pos_id }: ShortPdv) =>
          ({
            bnppf_pos_id,
            name,
            added:
              oldEmployee?.operational_seller_number_list?.includes(
                bnppf_pos_id
              ),
            partner_pos_id,
          } as ISelectNumeroAgrement)
      );
      setPdvs(prepData);
    }
  }, [oldEmployee, listPdvs]);
  function resetPsw() {
    resetPassword({
      siren: siren,
      empId: '' + searchParams.get('id'),
    })
      .unwrap()
      .then((payload) => {
        dispatch(
          setCreds({
            uid: payload.uid,
            user_password: payload.user_password,
          })
        );
        navigate('resetPassword');
      })
      .catch((err) => {
        //TODO traitement erreur
      });
  }

  return (
    <>
      {!(getOldEmpLoading || getListPdvsLoading) &&
        !(getOldEmpError || getListPdvsError) &&
        !(!employee || !oldEmployee || !listPdvs) && (
          <>
            <Form
              title="Modifier employé"
              employee={employee}
              pdvs={pdvs}
              setEmployee={setEmployee}
              setPdvs={setPdvs}
              uid={oldEmployee.uid}
              lastLoginTime={oldEmployee.last_login_time + ''}
              primaryBtnProps={{
                children: 'Modifier un employé',
                icon: <PlusCircle />,
                variant: 'primary',
                size: 'medium',
                onClick: (e) => {
                  e.preventDefault();
                  setOpenConfirmModal(true);
                },
              }}
              secondaryBtnProps={
                authn_method === 'LOCAL'
                  ? {
                      children: 'Réinitialiser le mot de passe',
                      variant: 'secondary',
                      type: 'button',
                      onClick: () => {
                        setOpenedResetPswModal(true);
                      },
                    }
                  : undefined
              }
            />
            <Modal
              primaryBtnProps={{
                loading: isLoading,
                children: 'Confirmer',
                onClick: handleSubmit,
              }}
              secondaryBtnProps={{
                children: 'Annuler',
                onClick: () => setOpenConfirmModal(false),
              }}
              title="Voulez-vous modifier l'employé ?"
              open={openConfirmModal}
              onClose={() => setOpenConfirmModal(false)}
            />
            <Modal
              primaryBtnProps={{
                loading: isResetPswLoading,
                children: 'Confirmer',
                onClick: resetPsw,
              }}
              secondaryBtnProps={{
                children: 'Annuler',
                onClick: () => setOpenedResetPswModal(false),
              }}
              title="Voulez-vous réinitialiser le mot de passe ?"
              open={openedResetPswModal}
              onClose={() => setOpenedResetPswModal(false)}
            />
          </>
        )}
    </>
  );
}
